import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import { CATEGORIES } from "@/lib/constants";
import { BookOpen, ArrowLeft, Plus, Pencil, Trash2, Search, Save, X } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", category: "", price_min: "", price_max: "", common_presentation: "", aliases: [] };

export default function AdminCatalogPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // { originalName, form }
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/catalog");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((it) =>
      it.name.toLowerCase().includes(q) ||
      it.category.toLowerCase().includes(q) ||
      (it.aliases || []).some((a) => a.toLowerCase().includes(q))
    );
  }, [items, search]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((it) => {
      map[it.category] = map[it.category] || [];
      map[it.category].push(it);
    });
    return map;
  }, [filtered]);

  const startCreate = () => { setEditing({ originalName: null, form: { ...empty } }); setOpen(true); };
  const startEdit = (it) => {
    setEditing({
      originalName: it.name,
      form: {
        name: it.name,
        category: it.category,
        price_min: String(it.price_min ?? ""),
        price_max: String(it.price_max ?? ""),
        common_presentation: it.common_presentation || "",
        aliases: [...(it.aliases || [])],
      },
    });
    setOpen(true);
  };

  const remove = async (name) => {
    try {
      await api.delete(`/admin/catalog/${encodeURIComponent(name)}`);
      toast.success("Medicamento eliminado del catálogo");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="bg-slate-50 min-h-[80vh]">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Link to="/admin" className="inline-flex items-center text-blue-700 hover:text-blue-900 text-sm font-medium mb-3" data-testid="admin-catalog-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver al panel
        </Link>

        <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-xl bg-blue-700 text-white grid place-items-center shadow">
              <BookOpen className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-display text-3xl sm:text-4xl font-semibold text-blue-950">Catálogo</h1>
              <p className="text-slate-600 mt-1 text-base">Administra los medicamentos disponibles para los formularios, con sus aliases comerciales.</p>
            </div>
          </div>
          <Button className="h-12 bg-blue-600 hover:bg-blue-700 rounded-xl" onClick={startCreate} data-testid="admin-catalog-new-btn">
            <Plus className="w-5 h-5 mr-1.5" /> Nuevo medicamento
          </Button>
        </div>

        <Card className="p-4 sm:p-5 rounded-2xl border border-slate-200 bg-white mb-5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre, alias o categoría…" className="h-12 pl-9 bg-slate-50" data-testid="admin-catalog-search-input" />
          </div>
        </Card>

        {loading ? (
          <div className="text-center text-slate-500 py-10">Cargando…</div>
        ) : Object.keys(grouped).length === 0 ? (
          <Card className="p-10 rounded-2xl border border-dashed border-slate-300 text-center" data-testid="admin-catalog-empty">
            <p className="text-slate-700">Sin resultados.</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {Object.keys(grouped).sort().map((cat) => (
              <Card key={cat} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="font-display font-semibold text-slate-900">{cat}</h3>
                  <Badge className="bg-blue-100 text-blue-900 hover:bg-blue-100">{grouped[cat].length}</Badge>
                </div>
                <div className="divide-y divide-slate-100">
                  {grouped[cat].map((it) => (
                    <div key={it.name} className="px-4 py-3 flex items-start gap-3 hover:bg-slate-50/60" data-testid={`admin-catalog-row-${it.name}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 text-sm">{it.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{it.common_presentation || "Sin presentación"} · <span className="text-blue-800 font-medium">${it.price_min}–${it.price_max} MXN</span></div>
                        {(it.aliases || []).length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {it.aliases.map((a) => (
                              <span key={a} className="px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-700 text-[11px] font-medium border border-slate-200">{a}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button variant="outline" size="sm" onClick={() => startEdit(it)} data-testid={`admin-catalog-edit-${it.name}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" className="border-rose-200 text-rose-700 hover:bg-rose-50" data-testid={`admin-catalog-delete-${it.name}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar {it.name}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Se quitará del catálogo y dejará de aparecer en el selector. Las publicaciones existentes no se eliminan.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(it.name)} className="bg-rose-600 hover:bg-rose-700" data-testid={`admin-catalog-delete-confirm-${it.name}`}>
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <CatalogEditor
        open={open}
        onOpenChange={setOpen}
        editing={editing}
        onSaved={() => { setOpen(false); load(); }}
      />
    </div>
  );
}

function CatalogEditor({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState(empty);
  const [aliasInput, setAliasInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) setForm(editing.form);
    setAliasInput("");
  }, [editing]);

  if (!editing) return null;
  const isEdit = !!editing.originalName;
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addAlias = () => {
    const v = aliasInput.trim();
    if (!v) return;
    if (form.aliases.some((a) => a.toLowerCase() === v.toLowerCase())) {
      setAliasInput("");
      return;
    }
    setForm((f) => ({ ...f, aliases: [...f.aliases, v] }));
    setAliasInput("");
  };
  const removeAlias = (a) =>
    setForm((f) => ({ ...f, aliases: f.aliases.filter((x) => x !== a) }));

  const onAliasKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addAlias();
    } else if (e.key === "Backspace" && !aliasInput && form.aliases.length) {
      setForm((f) => ({ ...f, aliases: f.aliases.slice(0, -1) }));
    }
  };

  const save = async () => {
    if (!form.name.trim() || !form.category) {
      toast.error("Nombre y categoría son obligatorios");
      return;
    }
    const min = parseFloat(form.price_min || "0");
    const max = parseFloat(form.price_max || "0");
    if (max < min) {
      toast.error("El precio máximo debe ser mayor o igual al mínimo");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        price_min: min,
        price_max: max,
        common_presentation: form.common_presentation.trim(),
        aliases: form.aliases,
      };
      if (isEdit) {
        await api.put(`/admin/catalog/${encodeURIComponent(editing.originalName)}`, payload);
        toast.success("Medicamento actualizado");
      } else {
        await api.post("/admin/catalog", payload);
        toast.success("Medicamento agregado");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="admin-catalog-editor">
        <DialogHeader>
          <DialogTitle className="font-display text-blue-950">{isEdit ? "Editar medicamento" : "Nuevo medicamento"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium text-slate-800">Nombre canónico</Label>
            <Input value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Ej. Paracetamol 500 mg" className="h-12 mt-1.5 bg-slate-50" data-testid="admin-catalog-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium text-slate-800">Categoría</Label>
              <Select value={form.category} onValueChange={(v) => setField("category", v)}>
                <SelectTrigger className="h-12 mt-1.5 bg-slate-50" data-testid="admin-catalog-category-select">
                  <SelectValue placeholder="Selecciona…" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-800">Presentación común</Label>
              <Input value={form.common_presentation} onChange={(e) => setField("common_presentation", e.target.value)} placeholder="Caja con 20 tabletas" className="h-12 mt-1.5 bg-slate-50" data-testid="admin-catalog-presentation-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium text-slate-800">Precio mínimo (MXN)</Label>
              <Input type="number" min={0} step="0.01" value={form.price_min} onChange={(e) => setField("price_min", e.target.value)} placeholder="0" className="h-12 mt-1.5 bg-slate-50" data-testid="admin-catalog-min-input" />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-800">Precio máximo (MXN)</Label>
              <Input type="number" min={0} step="0.01" value={form.price_max} onChange={(e) => setField("price_max", e.target.value)} placeholder="0" className="h-12 mt-1.5 bg-slate-50" data-testid="admin-catalog-max-input" />
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium text-slate-800">Aliases / nombres comerciales</Label>
            <div className="mt-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 min-h-[48px] flex items-center flex-wrap gap-1.5">
              {form.aliases.map((a) => (
                <span key={a} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-slate-200 text-xs text-slate-800" data-testid={`admin-catalog-alias-${a}`}>
                  {a}
                  <button type="button" onClick={() => removeAlias(a)} className="text-slate-400 hover:text-rose-600" aria-label={`Quitar ${a}`}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <input
                value={aliasInput}
                onChange={(e) => setAliasInput(e.target.value)}
                onKeyDown={onAliasKey}
                onBlur={addAlias}
                placeholder={form.aliases.length === 0 ? "Tempra, Tylenol… (Enter para añadir)" : ""}
                className="flex-1 min-w-[120px] bg-transparent outline-none px-1 py-1 text-sm"
                data-testid="admin-catalog-alias-input"
              />
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Presiona <strong>Enter</strong> o <strong>,</strong> para añadir cada alias. Sirven para que el buscador y el matcher detecten nombres comerciales.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="admin-catalog-save-btn">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
